import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import { Alert, Button, Chip, CircularProgress } from '@mui/material';
import { GuestPushNotificationService } from '../services/GuestPushNotificationService';

/**
 * Admin push notifications history + entry point to the composer.
 * Send UI lives in GuestPushNotificationCreate.
 */
export default function GuestPushNotificationsScreen() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await GuestPushNotificationService.listHistory(100);
      setItems(rows);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex w-full h-screen overflow-auto">
      <div className="flex flex-col w-full mx-auto bg-[#F5F5F5]">
        <div className="px-10 bg-white flex items-center justify-between">
          <AdminHeader
            heading={'Push Notifications'}
            pages={'Pages / Guest Push Notifications'}
          />
          <Button variant="contained" onClick={() => navigate('/guest-push/create')}>
            New notification
          </Button>
        </div>

        <div className="px-10 py-6">
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

          {loading ? (
            <div className="flex justify-center py-24"><CircularProgress /></div>
          ) : items.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center text-gray-500">
              No notifications sent yet. Click “New notification” to send your first.
            </div>
          ) : (
            <div className="bg-white rounded-xl">
              <table className="w-full text-sm">
                <thead className="text-left text-gray-500">
                  <tr>
                    <th className="py-3 px-4">Title</th>
                    <th className="py-3 px-4">Target</th>
                    <th className="py-3 px-4">Sent</th>
                    <th className="py-3 px-4">Failed</th>
                    <th className="py-3 px-4">When</th>
                    <th className="py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((n) => (
                    <tr key={n._id} className="border-t border-gray-100 align-top">
                      <td className="py-3 px-4">
                        <div className="font-semibold">{n.title}</div>
                        <div className="text-gray-500 max-w-md truncate">{n.body}</div>
                      </td>
                      <td className="py-3 px-4">
                        <Chip size="small" label={n.target?.type || '—'} />
                      </td>
                      <td className="py-3 px-4">{n.stats?.sent ?? 0}</td>
                      <td className="py-3 px-4">{n.stats?.failed ?? 0}</td>
                      <td className="py-3 px-4 text-gray-500">
                        {n.sentAt ? new Date(n.sentAt).toLocaleString() : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <Chip
                          size="small"
                          color={n.status === 'sent' ? 'success' : n.status === 'failed' ? 'error' : 'default'}
                          label={n.status}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
